import { faDownload, faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ComponentType,
} from 'react';
import type { ActionMode } from 'src/components/action-mode';
import { useActionMode } from 'src/components/action-mode';
import type { ActionGroupMode } from 'src/components/buttons-and-actions/action-group';
import { useActionGroupMode } from 'src/components/buttons-and-actions/action-group';
import type { IconProps } from 'src/components/media-and-icons/icon';
import {
  getDefaultLinkStyles,
  getDefaultUnderlinedLinkStyles,
  getNavigationItemStyles,
} from 'src/components/navigation/link';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';

export type Size = 'default' | 'small';

type BaseButtonProps = {
  icon?: ComponentType<IconProps>;
};

export type ModeButtonProps = BaseButtonProps &
  (
    | {
        mode:
          | 'inverse' // maybe we can delete this when we delete the legacy styles (only used in menu)
          | 'icon'
          | 'icon-inverse'
          | 'embedded' // maybe we can delete this when we delete the legacy styles (only used in toast)
          | 'dropdown-item'
          | 'link';
        /** This mode doesn't use `icon`, but you can pass it, in case mode is switched on dynamically. */
        icon?: ComponentType<IconProps>;
      }
    | {
        mode?: 'tab';
        isActive?: boolean;
      }
    | {
        mode: 'segmented-control';
        size?: Size;
        isSelected?: boolean;
      }
    | {
        mode: 'link-underlined';
        behaviour?: 'external' | 'download';
      }
    | {
        mode: 'navigation-item'; // used in the app shell navigation
        icon?: ComponentType<IconProps>;
        isActive?: boolean;
        hideIcon?: undefined;
      }
    | {
        mode?: 'primary';
        size?: Size;
      }
    | {
        mode: 'secondary';
        size?: Size;
      }
    | {
        mode: 'link-primary'; // maybe we can delete this when we delete the legacy styles (only used in the header)
      }
  );

export type ButtonProps = {
  /**
   * Configure your button type.
   *
   * @default 'button'
   */
  type?: string;
} & ModeButtonProps &
  ComponentPropsWithoutRef<'button'>;

// helper type to allow easier access to the custom props
type CustomButtonProps = {
  mode: ModeButtonProps['mode'];
  behaviour?: 'external' | 'download';
  size?: Size;
  isActive?: boolean;
  isSelected?: boolean;
};

/**
 * Your basic button component. Use this for `onClick` interactions.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (baseProps, ref) => {
    const actionMode = useActionMode();
    const actionGroupMode = useActionGroupMode();
    const isNewDesignActive = useIsNewDesignActive();

    // descruct all custom props
    const {
      mode,
      icon: IconComponent,
      behaviour,
      size,
      isActive,
      isSelected,
      ...props
    } = {
      type: 'button',
      ...mapActionModeToButton(actionMode),
      ...baseProps,
    } as ButtonProps & CustomButtonProps;

    switch (mode) {
      case 'link-underlined': {
        return (
          <span>
            <button
              {...props}
              className={getDefaultUnderlinedLinkStyles({
                className: props.className,
              })}
              ref={ref}
            />
            {behaviour && (
              <>
                {' '}
                <FontAwesomeIcon
                  className="not-legacy:text-foreground-primary-inverted"
                  icon={behaviour === 'download' ? faDownload : faExternalLink}
                />
              </>
            )}
          </span>
        );
      }
      case 'link':
        return (
          <button
            {...props}
            className={getDefaultLinkStyles({
              className: cn(
                `
                  text-left
                  legacy:border-b-2 legacy:border-transparent
                  legacy:leading-[inherit]
                `,
                props.className
              ),
            })}
            ref={ref}
          />
        );
      case 'tab':
        return (
          <button
            {...props}
            className={getTabClasses({
              isActive,
              className: cn(
                'legacy:border-b-2 legacy:border-transparent',
                props.className
              ),
            })}
            ref={ref}
            disabled={props.disabled ?? isActive}
          />
        );
      case 'segmented-control':
        return (
          <button
            {...props}
            className={getButtonSecondaryClasses({
              isSelected,
              actionGroupMode,
              size: 'small',
              className: props.className,
            })}
            ref={ref}
            disabled={props.disabled ?? isSelected}
          />
        );
      case 'link-primary':
        return (
          <button
            {...props}
            className={cn(
              getBaseActionClasses({ isActive }),
              `
                border-b-2 border-none border-transparent bg-transparent
                text-left text-white
              `,
              !props.disabled &&
                `
                  hover:border-b-white
                  focus:border-b-white
                `,
              props.className
            )}
            ref={ref}
          />
        );
      case 'navigation-item':
        return (
          <button
            {...props}
            className={cn(
              getNavigationItemStyles({ isActive }),
              props.className
            )}
            ref={ref}
          >
            {IconComponent && (
              <IconComponent size="sm" className={cn(`shrink-0`)} />
            )}

            <div
              className={cn(
                `
                  grow truncate
                  group-data-[sidebar-expanded=true]:group-data-[sidebar-transitioning=false]:truncate-none
                `,
                // Allow the button to have adjacent elements like a chevron icon
                // for the navigation child group items or a dropdown ellipsis icon
                `flex justify-between`
              )}
            >
              {props.children}
            </div>
          </button>
        );
      case 'embedded':
        return (
          <button
            {...props}
            className={cn(
              getBaseActionClasses({ isActive }),
              `
                border-none bg-none text-legacy-brand opacity-40
                transition-opacity duration-200
              `,
              !props.disabled &&
                `
                  hover:opacity-80
                  focus:opacity-80
                `,
              props.className
            )}
            ref={ref}
          />
        );
      case 'icon':
        return (
          <button
            {...props}
            className={cn(
              getBaseActionClasses({ isActive }),
              `
                border-none bg-transparent text-label-base
                text-foreground-secondary-default
                not-disabled:not-active:hover:text-foreground-secondary-hover
                focus-visible:spaced-focus-style
                active:text-foreground-secondary-active
                disabled:text-foreground-secondary-disabled
              `,
              `
                aria-pressed:bg-background-surface-selected
                aria-pressed:text-foreground-surface-selected!
                aria-pressed:hover:bg-background-surface-selected
              `,
              `
                legacy:aria-pressed:bg-legacy-brand
                legacy:aria-pressed:text-white!
                legacy:aria-pressed:hover:bg-legacy-brand
              `,
              `
                legacy:text-[1.6rem] legacy:text-legacy-brand
                legacy:not-disabled:not-active:hover:text-legacy-brand-secondary
                legacy:not-disabled:not-active:focus:text-legacy-brand-secondary
                legacy:active:text-legacy-brand-secondary
                legacy:disabled:text-legacy-inactive
              `,
              props.className
            )}
            ref={ref}
          />
        );
      case 'icon-inverse':
        return (
          <button
            {...props}
            className={cn(
              getBaseActionClasses({ isActive }),
              `
                border-none bg-transparent text-label-base
                text-foreground-primary-default
                not-disabled:not-active:hover:text-background-secondary-default
                active:text-foreground-secondary-active
                disabled:text-foreground-secondary-disabled
              `,
              `
                legacy:bg-legacy-brand legacy:text-[1.6rem] legacy:text-white
                legacy:not-disabled:not-active:hover:text-legacy-brand-secondary
                legacy:not-disabled:not-active:focus:text-legacy-brand-secondary
                legacy:active:text-legacy-brand-secondary
                legacy:disabled:text-legacy-inactive
              `,
              props.className
            )}
            ref={ref}
          />
        );
      case 'dropdown-item':
        return (
          <button
            {...props}
            className={getDropdownItemClasses({
              className: props.className,
              isActive,
            })}
            ref={ref}
          />
        );
      case 'inverse':
        return (
          <button
            {...props}
            className={getButtonInverseClasses({
              actionGroupMode,
              className: props.className,
            })}
            ref={ref}
          />
        );
      case 'secondary':
        return (
          <button
            {...props}
            className={getButtonSecondaryClasses({
              isSelected: false,
              actionGroupMode,
              size,
              className: props.className,
            })}
            ref={ref}
            disabled={props.disabled ?? isActive}
          />
        );
      case 'primary':
      case undefined:
        return (
          <button
            {...props}
            className={getButtonPrimaryClasses({
              actionGroupMode,
              size,
              className: props.className,
            })}
            ref={ref}
          />
        );
    }
  }
);

export function getTabClasses({
  isActive,
  className,
}: {
  isActive?: boolean;
  className?: string;
}) {
  return cn(
    getBaseActionClasses({ isActive }),
    `
      -mb-px px-md pt-sm pb-lg text-body-base text-foreground-surface-subdued
      no-underline
      not-legacy:relative
      not-legacy:after:absolute not-legacy:after:bottom-0
      not-legacy:after:left-0 not-legacy:after:w-full
      not-legacy:after:border-b-2 not-legacy:after:border-b-transparent
      not-legacy:not-disabled:not-active:hover:after:border-b-foreground-surface-subdued
      active:text-foreground-default
      not-legacy:active:after:border-b-border-surface-default
    `,
    `
      legacy:border-b-2 legacy:border-b-transparent legacy:p-0
      legacy:text-[1.6rem] legacy:leading-[1.42857] legacy:text-legacy-brand
      legacy:hover:border-b-legacy-brand legacy:hover:text-legacy-brand
      legacy:focus:border-b-legacy-brand legacy:focus:text-legacy-brand
    `,
    isActive &&
      `
        not-legacy:font-body-semibold not-legacy:text-foreground-default
        not-legacy:after:border-b-border-surface-default
        not-legacy:not-disabled:not-active:hover:after:border-b-border-surface-default
        legacy:border-b-legacy-brand legacy:text-legacy-brand
      `,
    className
  );
}

export function getDropdownItemClasses({
  className,
  isActive,
}: {
  className?: string;
  isActive?: boolean;
}) {
  return cn(
    getBaseActionClasses({ isActive }),
    `
      inline-block w-full rounded-xs bg-background-surface-default px-lg py-sm
      text-left text-label-base text-foreground-default
      not-legacy:border-none
      not-disabled:not-active:hover:bg-background-surface-hover
      active:bg-background-surface-selected
      active:text-foreground-surface-selected
      disabled:bg-background-surface-disabled
      disabled:text-foreground-surface-disabled
    `,
    `
      focus-visible:active:contrast-focus-style
      not-legacy:aria-pressed:bg-background-surface-selected!
      not-legacy:aria-pressed:text-foreground-surface-selected!
      not-legacy:aria-pressed:hover:bg-background-surface-selected
    `,
    `
      legacy:aria-pressed:bg-[#f5f5f5] legacy:aria-pressed:text-[#333]!
      legacy:aria-pressed:hover:bg-[#f5f5f5]
      legacy:aria-pressed:hover:text-[#333]
    `,
    `
      legacy:rounded-none legacy:bg-transparent legacy:px-[2rem]
      legacy:py-[0.2rem] legacy:text-legacy-base legacy:text-[#333]
      legacy:not-disabled:not-active:hover:bg-[#f5f5f5]
      legacy:not-disabled:not-active:hover:text-[#333]
      legacy:not-disabled:not-active:focus:bg-[#f5f5f5]
      legacy:not-disabled:not-active:focus:text-[#333]
      legacy:active:bg-[#f5f5f5] legacy:active:text-[#333]
    `,
    isActive &&
      `
        not-legacy:bg-background-surface-selected
        not-legacy:text-foreground-surface-selected
        not-legacy:hover:bg-background-surface-selected!
        focus-visible:contrast-focus-style
        legacy:bg-[#f5f5f5] legacy:text-[#333]
        legacy:hover:bg-[#f5f5f5] legacy:hover:text-[#333]
      `,
    className
  );
}

export function getButtonInverseClasses({
  actionGroupMode,
  className,
}: {
  actionGroupMode: ActionGroupMode;
  className?: string;
}) {
  return cn(
    getBaseActionClasses({ isActive: undefined }),
    getActionGroupModeClasses({ actionGroupMode, size: undefined }),
    `
      bg-background-primary-inverted text-center font-label-semibold
      text-foreground-primary-inverted
      not-legacy:border-none
      not-disabled:not-active:hover:bg-background-primary-inverted
      not-disabled:not-active:hover:text-foreground-secondary-hover
      active:bg-background-primary-inverted
      active:text-foreground-secondary-active
      disabled:bg-background-primary-inverted
      disabled:text-foreground-primary-disabled
    `,
    getActionSizeClasses({ size: undefined }),
    `
      legacy:border-2 legacy:border-white legacy:bg-white
      legacy:font-legacy-bold legacy:text-legacy-brand
      legacy:not-disabled:not-active:hover:border-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:bg-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:text-white
      legacy:not-disabled:not-active:focus:border-legacy-brand-secondary
      legacy:not-disabled:not-active:focus:bg-legacy-brand-secondary
      legacy:not-disabled:not-active:focus:text-white
      legacy:active:border-legacy-brand-secondary
      legacy:active:bg-legacy-brand-secondary legacy:active:text-white
      legacy:active:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]
    `,
    className
  );
}

export function getButtonSecondaryClasses({
  isSelected,
  actionGroupMode,
  size,
  className,
}: {
  isSelected?: boolean;
  actionGroupMode: ActionGroupMode;
  size?: Size;
  className?: string;
}) {
  return cn(
    getBaseActionClasses({ isActive: isSelected }),
    getActionGroupModeClasses({ actionGroupMode, size }),
    `
      bg-background-secondary-default text-center font-label-semibold
      text-foreground-secondary-default
      not-legacy:border-none
      not-disabled:not-active:hover:bg-background-secondary-hover
      not-disabled:not-active:hover:text-foreground-secondary-hover
      focus-visible:contrast-focus-style
      active:bg-background-secondary-active
      active:text-foreground-secondary-active
      disabled:bg-background-secondary-disabled
      disabled:text-foreground-secondary-disabled
    `,
    isSelected &&
      `
        not-legacy:active:bg-background-surface-active
        not-legacy:active:text-foreground-primary-default
        not-legacy:disabled:bg-background-surface-active
        not-legacy:disabled:text-foreground-primary-default
      `,
    `
      aria-pressed:bg-background-secondary-selected
      aria-pressed:text-foreground-secondary-selected!
      aria-pressed:hover:bg-background-secondary-selected!
    `,
    `
      legacy:aria-pressed:bg-legacy-brand-secondary
      legacy:aria-pressed:text-white!
      legacy:aria-pressed:hover:bg-legacy-brand-secondary
    `,
    getActionSizeClasses({ size }),
    `
      legacy:border-2 legacy:border-transparent legacy:bg-legacy-brand-light-3
      legacy:font-legacy-bold legacy:text-legacy-brand
      legacy:not-disabled:not-active:hover:border-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:bg-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:text-white
      legacy:not-disabled:focus:border-legacy-brand-secondary
      legacy:not-disabled:focus:bg-legacy-brand-secondary
      legacy:not-disabled:focus:text-white
      legacy:active:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]
      legacy:disabled:bg-legacy-brand-light-3 legacy:disabled:text-legacy-brand
    `,
    isSelected && 'legacy:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]',
    className
  );
}

export function getButtonPrimaryClasses({
  actionGroupMode,
  size,
  className,
}: {
  actionGroupMode: ActionGroupMode;
  size?: Size;
  className?: string;
}) {
  return cn(
    getBaseActionClasses({ isActive: undefined }),
    getActionGroupModeClasses({ actionGroupMode, size }),
    `
      bg-background-primary-default text-center font-label-semibold
      text-foreground-primary-default
      not-legacy:border-none
      not-disabled:not-active:hover:bg-background-primary-hover
      not-disabled:not-active:hover:text-foreground-primary-hover
      focus-visible:contrast-focus-style
      active:bg-background-primary-active active:text-foreground-primary-active
      disabled:bg-background-primary-disabled
      disabled:text-foreground-primary-default
    `,
    `
      aria-pressed:bg-background-primary-selected
      aria-pressed:text-foreground-primary-selected!
      aria-pressed:hover:bg-background-primary-selected!
    `,
    `
      legacy:aria-pressed:bg-legacy-brand legacy:aria-pressed:text-white!
      legacy:aria-pressed:hover:bg-legacy-brand
    `,
    getActionSizeClasses({ size }),
    `
      legacy:border-2 legacy:border-legacy-brand legacy:bg-legacy-brand
      legacy:font-legacy-bold legacy:text-white
      legacy:not-disabled:not-active:hover:border-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:bg-legacy-brand-secondary
      legacy:not-disabled:not-active:hover:text-white
      legacy:not-disabled:focus:border-legacy-brand-secondary
      legacy:not-disabled:focus:bg-legacy-brand-secondary
      legacy:not-disabled:focus:text-white
      legacy:active:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]
      legacy:disabled:bg-legacy-brand legacy:disabled:text-white
    `,
    className
  );
}

/**
 * These classes form the base style of buttons and links.
 */
export function getBaseActionClasses({ isActive }: { isActive?: boolean }) {
  return cn(
    `
      cursor-pointer rounded-xs transition-[color,border-color,background-color]
      duration-150
      legacy:rounded-none legacy:duration-350
    `,
    !isActive &&
      `
        disabled:cursor-not-allowed
        legacy:disabled:opacity-65 legacy:disabled:shadow-none
      `,
    isActive && 'disabled:cursor-default'
  );
}

export function getActionGroupModeClasses({
  actionGroupMode,
  size,
}: {
  actionGroupMode: ActionGroupMode;
  size?: Size;
}) {
  return cn(
    actionGroupMode === 'left' &&
      size === 'small' &&
      `
        rounded-l-3xl rounded-r-none
        legacy:rounded-l-[1.6rem]
      `,
    actionGroupMode === 'left' &&
      size !== 'small' &&
      `
        rounded-l-3xl rounded-r-none
        legacy:rounded-l-[1.8rem]
      `,
    actionGroupMode === 'right' &&
      size === 'small' &&
      `
        rounded-l-none rounded-r-3xl
        legacy:rounded-r-[1.6rem]
      `,
    actionGroupMode === 'right' &&
      size !== 'small' &&
      `
        rounded-l-none rounded-r-3xl
        legacy:rounded-r-[1.8rem]
      `,
    actionGroupMode === 'single' &&
      size === 'small' &&
      `
        rounded-3xl
        legacy:rounded-[1.6rem]
      `,
    actionGroupMode === 'single' &&
      size !== 'small' &&
      `
        rounded-3xl
        legacy:rounded-[1.8rem]
      `,
    actionGroupMode === 'left' && 'mr-[0.1rem]',
    actionGroupMode === 'middle' && 'mr-[0.1rem] rounded-none'
  );
}

export function getActionSizeClasses({ size }: { size?: Size }) {
  return cn(
    size === 'small'
      ? `
        px-lg py-xs text-label-sm
        legacy:px-[1rem] legacy:py-[0.4rem] legacy:text-legacy-xs
        legacy:leading-none
      `
      : `
        px-xl py-md text-label-base
        legacy:px-[2rem] legacy:py-[0.6rem] legacy:text-legacy-base
      `
  );
}

export function mapActionModeToButton(
  actionMode: ActionMode | null
): ModeButtonProps | null {
  if (!actionMode) return null;

  switch (actionMode.mode) {
    case 'button-primary':
      return { ...actionMode, mode: 'primary' };
    case 'button-secondary':
      return { ...actionMode, mode: 'secondary' };
    case 'link-primary':
      return { ...actionMode, mode: 'link-primary' };
    case 'dropdown':
      return { ...actionMode, mode: 'dropdown-item' };
    case 'tab':
      return { ...actionMode, mode: 'tab' };
    case 'navigation-item':
      return { ...actionMode, mode: 'navigation-item' };
    case 'segmented-control':
      return { ...actionMode, mode: 'segmented-control' };
    case 'sidebar-item':
      throw new Error(
        'Buttons currently do not support action mode="sidebar".'
      );
    case 'navigation-child-item':
      throw new Error(
        'Buttons currently do not support action mode="navigation-child-item".'
      );
  }
}
